/**
 * Como o painel organiza o que está em cache — lógica pura, sem React.
 *
 * Separado da tela pelo mesmo motivo que o coletor (services/devtools.js):
 * agrupar por domínio e tornar a chave legível são regras que dá para testar
 * sozinhas, sem montar componente.
 *
 * `montarArvore` aceita o estado vivo por parâmetro para o teste poder passar
 * o seu — em teste não há cache de verdade rodando.
 */
import { requestCache } from "../../services/requestCache";

/** "contacts:{...}" -> "contacts". O que vem antes do primeiro ":" é o domínio. */
export const dominioDe = chave => String(chave).split(":")[0];

/**
 * O resto da chave, legível.
 *
 * A chave crua é o JSON dos parâmetros — mostrar
 * `{"searchParam":"","pageNumber":2,"contactTag":"[]"}` inteiro em cada linha
 * era o que tornava a lista ilegível quando a rolagem infinita de contatos
 * criava uma chave por página. Aqui sobra o que DISTINGUE: parâmetro vazio não
 * diferencia nada, então sai.
 */
export const rotuloDaChave = chave => {
  const resto = String(chave).slice(dominioDe(chave).length + 1);
  if (!resto) return "(sem parâmetro)";
  try {
    const obj = JSON.parse(resto);
    if (obj === null || typeof obj !== "object") return String(resto);
    const uteis = Object.entries(obj).filter(
      ([, v]) => v !== "" && v !== null && v !== undefined && v !== "[]"
    );
    if (uteis.length === 0) return "(sem parâmetro)";
    return uteis.map(([k, v]) => `${k} ${v}`).join("  ");
  } catch {
    return resto;
  }
};

/**
 * Junta o que o cache guarda (contadores) com o que ele AINDA guarda
 * (validade), agrupado por domínio.
 *
 * São duas perguntas diferentes: os contadores dizem quanto a chave rendeu; a
 * validade diz por que a tela pode não ter atualizado ainda — o dado continua
 * dentro do TTL até alguém invalidar.
 */
export const montarArvore = (linhas, estadoVivo = requestCache.estado()) => {
  const vivos = estadoVivo;
  const porChave = new Map(vivos.map(v => [v.chave, v]));
  const contadores = new Map(linhas.map(l => [l.chave, l]));
  const chaves = [...new Set([...vivos.map(v => v.chave), ...linhas.map(l => l.chave)])];

  const dominios = new Map();
  chaves.forEach(chave => {
    const nome = dominioDe(chave);
    const c = contadores.get(chave) || { hit: 0, compartilhada: 0, miss: 0 };
    const vivo = porChave.get(chave);

    const atual = dominios.get(nome) || {
      nome,
      hit: 0,
      compartilhada: 0,
      miss: 0,
      chaves: []
    };
    atual.hit += c.hit;
    atual.compartilhada += c.compartilhada;
    atual.miss += c.miss;
    atual.chaves.push({
      chave,
      ...c,
      restanteMs: vivo ? vivo.restanteMs : null,
      valor: vivo ? vivo.valor : undefined
    });
    dominios.set(nome, atual);
  });

  return [...dominios.values()]
    .map(d => ({
      ...d,
      chaves: d.chaves.sort((a, b) => b.hit + b.compartilhada - (a.hit + a.compartilhada))
    }))
    .sort((a, b) => b.hit + b.compartilhada - (a.hit + a.compartilhada));
};

